import { useEffect, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Typography } from "@powerdiary/components-library/lib";
import { useClientPortalSelector, useClientPortalDispatch, selectBooking, setLocation } from "../../../../redux";
import { ILocationData } from "../../bookingSlice.types";
import ListItem from "../ListItem";
import styles from "./Location.module.css";
import { useLocations } from "../../hooks/useLocations";
import { LocationBookingModel } from "types/generated/models";
import { useAnalytics } from "hooks/useAnalytics";
import { useBookingNavigation } from "../../hooks/useBookingNavigation";

export default function Location() {
    const { location, session } = useClientPortalSelector(selectBooking);
    const dispatch = useClientPortalDispatch();
    const { locations, isLoading } = useLocations();
    const { track } = useAnalytics();
    const { handleNext } = useBookingNavigation();

    const selectedLocationId = location.data?.locationId || null;

    const selectLocationAndProceed = useCallback(
        (locationData: ILocationData, autoSelected: boolean, locationModel: LocationBookingModel) => {
            dispatch(setLocation(locationData));
            track("booking_step_location_completed", {
                booking_type: session.bookingType,
                location_id: locationModel.id,
                location_name: locationModel.name,
                auto_selected: autoSelected,
            });
            handleNext();
        },
        [dispatch, track, session.bookingType, handleNext]
    );

    useEffect(() => {
        if (locations?.length === 1 && !selectedLocationId) {
            const singleLocation = locations[0];
            const locationData: ILocationData = {
                locationId: singleLocation.id || 0,
                locationName: singleLocation.name || "",
            };

            selectLocationAndProceed(locationData, true, singleLocation);
        }
    }, [locations, selectedLocationId, selectLocationAndProceed]);

    const handleLocationSelect = (selectedLocation: LocationBookingModel) => {
        const locationData: ILocationData = {
            locationId: selectedLocation.id || 0,
            locationName: selectedLocation.name || "",
        };

        selectLocationAndProceed(locationData, false, selectedLocation);
    };

    return (
        <Box className={styles.wrapper} data-test-id="section_location">
            <Typography mb={1} variant="heading4" data-test-id="text_location_title">
                Select a location
            </Typography>
            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4} data-test-id="loading_indicator_locations">
                    <CircularProgress data-test-id="loading_spinner" />
                </Box>
            ) : (
                <>
                    {locations?.length > 0 ? (
                        <Box className={styles.listWrapper} data-test-id="section_location_list">
                            {locations.map((location) => (
                                <ListItem
                                    key={location.id}
                                    title={location.name || ""}
                                    selected={selectedLocationId === location.id}
                                    onClick={() => handleLocationSelect(location)}
                                    data-test-id={`location_item_${location.id}`}
                                    dataTestIdSuffix={`location_${location.id}`}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="paragraphMediumSemiBold"
                            textAlign="center"
                            data-test-id="text_no_locations_message"
                        >
                            No locations are available.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
}
